import { FC, useEffect, useMemo, useState } from 'react'
import cn from './index.module.css'
import CoursesBanner from '@pages/CoursesCatalog/components/CoursesBanner/CoursesBanner'
import useHorizontalSorting, {
  SortingTab,
  SortOrder,
} from '@pages/CoursesCatalog/components/HorizontalSorting/useHorizontalSorting'
import HorizontalSorting from '@pages/CoursesCatalog/components/HorizontalSorting/HorizontalSorting'
import FolderList from '@pages/CoursesCatalog/components/FolderList/FolderList'
import CoursesList from '@pages/CoursesCatalog/components/CoursesList/CoursesList'
import { fetchCourses } from './api'
import { useGetSettings } from './hooks/getPlatformSettings'
import CourseEnrollmentModal from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/CourseEnrollmentModal'
import Preloader from '@UI/Preloader'
import { useSearchParams } from '@/hooks/useSearchParams'
import {
  fetchCatalogFolders,
  fetchSubFolders,
} from '@pages/CoursesCatalog/components/FolderList/api'
import ClassEnrollmentModal from '@pages/CoursesCatalog/modals/ClassEnrollmentModal/ClassEnrollmentModal'
import { crmApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { AuthService } from '@/services/AuthService'

interface ICatalogCoursesTab {}

const CatalogCoursesTab: FC<ICatalogCoursesTab> = ({}) => {
  const addAlert = useAddAlert()
  const { checkSettings } = useGetSettings()
  const [folder, setFolder] = useSearchParams('folder')
  const [subFolder, setSubFolder, removeSubFolder] =
    useSearchParams('sub_folder')
  const [orderSP, setOrderSP, removeOrderSP] = useSearchParams('order')
  const [sortingSP, setSortingSP, removeSortingSP] = useSearchParams('sorting')
  const showCourseSorting = checkSettings('enableSortingOfCoursesInCatalog')
  const [isLoading, setIsLoading] = useState(true)
  const [courses, setCourses] = useState<ICourse[]>([])
  const [courseEnroll, setCourseEnroll] = useState<{
    courseId: number
    tariffId: number
    crmLinkId?: number
    userId?: number
  } | null>(null)
  const [classEnroll, setClassEnroll] = useState<{
    classId?: number
    crmLinkId?: number
    userId?: number
  } | null>(null)
  const sortingTabs = useFilteredSortingTabs()
  const sortingState = useHorizontalSorting({ tabs: sortingTabs })
  const [isLoadingFolders, setIsLoadingFolders] = useState<boolean>(true)
  const [folders, setFolders] = useState<IFolder[]>([])
  const [subFolders, setSubFolders] = useState<ISubFolder[]>([])

  const selectedFolder: number | undefined =
    folder === null || folder.trim() === '' || isNaN(Number(folder))
      ? undefined
      : Number(folder)

  const selectedSubFolder: number | undefined =
    subFolder === null || subFolder.trim() === '' || isNaN(Number(subFolder))
      ? undefined
      : Number(subFolder)

  const fetch = async () => {
    setIsLoadingFolders(true)
    const folders = await fetchCatalogFolders()
    const subFolders = await fetchSubFolders()

    setFolders(folders)
    setSubFolders(subFolders)

    if (!selectedFolder) {
      setFolder(folders?.[0]?.id)
    }
    setIsLoadingFolders(false)
  }

  useEffect(() => {
    sortingState.setTabs(sortingTabs)
  }, [sortingTabs])

  useEffect(() => {
    if (sortingSP) {
      sortingState.toggleTab(sortingSP, orderSP as SortOrder)
    }
  }, [sortingSP, orderSP, sortingTabs])

  useEffect(() => {
    if (sortingState?.sorting?.[0]) {
      setSortingSP(sortingState?.sorting?.[0].id)
      if (sortingState?.sorting?.[0]?.order !== 'none') {
        setOrderSP(sortingState?.sorting?.[0]?.order)
      } else {
        removeOrderSP()
      }
    }
  }, [sortingState.sorting])

  const onSort = (tab: SortingTab) => {
    sortingState.toggleTab(tab.id)
  }

  useEffect(() => {
    async function fetch() {
      if (!folder && !subFolder) {
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      const courses = await fetchCourses({
        folder: Number(folder),
        subFolder: Number(subFolder),
        sorting: sortingState.sorting?.[0],
      })
      setCourses(courses)
      setIsLoading(false)
    }

    fetch()
  }, [folder, subFolder, sortingState.sorting])

  const openLinkEnrollmentModal = async () => {
    const params = new URLSearchParams(window.location.search)
    const courseId = params.get('course_id')
    const tariffId = params.get('tariff_id')
    const classId = params.get('class_id')
    const crmLinkId = params.get('crm_link_id')
    const userId = params.get('user_id')

    if (crmLinkId) {
      try {
        await AuthService.logout() // разлогиниваем пользователя, если с crm

        const srmLink = (await crmApi.getLeadPaymentLink(Number(crmLinkId!)))
          ?.data?.data

        if (!srmLink || srmLink.status == 'completed') {
          addAlert({ text: 'Ссылка для покупки невалидна' })
          return
        }
        if (courseId && tariffId) {
          setCourseEnroll({
            courseId: Number(courseId),
            tariffId: Number(tariffId),
            crmLinkId: Number(crmLinkId),
            userId: userId ? Number(userId) : undefined,
          })
        } else if (classId) {
          setClassEnroll({
            crmLinkId: Number(crmLinkId),
            userId: userId ? Number(userId) : undefined,
          })
        }
        return
      } catch (error) {
        addAlert({ text: 'Ссылка для покупки невалидна' })
        return
      }
    }

    if (courseId && tariffId) {
      setCourseEnroll({
        courseId: Number(courseId),
        tariffId: Number(tariffId),
        userId: userId ? Number(userId) : undefined,
      })
    } else if (classId) {
      setClassEnroll({
        classId: Number(classId),
        userId: userId ? Number(userId) : undefined,
      })
    }
  }

  useEffect(() => {
    fetch()
    openLinkEnrollmentModal()
  }, [])

  const onEnrollmentDismiss = () => {
    setCourseEnroll(null)
    setClassEnroll(null)
    window.history.replaceState(null, '', window.location.pathname)
  }

  return (
    <>
      <div className={cn.wrapper}>
        <CoursesBanner />
        {showCourseSorting && sortingTabs?.length > 0 && (
          <HorizontalSorting
            label="Сортировать по:"
            tabs={sortingState.state}
            onClick={onSort}
          />
        )}
        <div className={cn.listLayout}>
          <FolderList
            className={cn.folders}
            folders={folders}
            subFolders={subFolders}
            isLoading={isLoadingFolders}
            selectedFolder={selectedFolder}
            selectedSubFolder={selectedSubFolder}
            onSelect={(value) => {
              setFolder(value.folder ?? '')
              if (!value.subFolder) {
                removeSubFolder()
              } else {
                setSubFolder(value.subFolder ?? '')
              }
            }}
          />
          {isLoading ? (
            <div className={cn.courseListLoadingStub}>
              <Preloader />
            </div>
          ) : (
            <CoursesList courses={courses} />
          )}
        </div>
      </div>
      <CourseEnrollmentModal
        visible={!!courseEnroll}
        crmLinkId={courseEnroll?.crmLinkId}
        userId={courseEnroll?.userId}
        courseId={courseEnroll?.courseId ?? 0}
        tariffId={courseEnroll?.tariffId ?? 0}
        onDismiss={onEnrollmentDismiss}
        onSuccess={() => {}}
      />
      <ClassEnrollmentModal
        visible={!!classEnroll}
        classId={classEnroll?.classId}
        classLinkId={classEnroll?.crmLinkId}
        userId={classEnroll?.userId}
        onDismiss={onEnrollmentDismiss}
        onSuccess={() => {}}
      />
    </>
  )
}

const useFilteredSortingTabs = (): SortingTab[] => {
  const { checkSettings } = useGetSettings()
  const hidePopularity = checkSettings('sortingCoursesInCatalogByPopularity')
  const hidePrice = checkSettings('sortingCoursesInCatalogByPrice')
  const hideDuration = checkSettings('sortingCoursesInCatalogByDuration')
  const hideDemo = checkSettings('sortingCoursesInCatalogByDemoAccess')

  const tabs = useMemo(() => {
    const tabs = []
    if (hidePopularity)
      tabs.push({ id: 'popularity', label: 'По популярности' })
    if (hidePrice) tabs.push({ id: 'price', label: 'По цене' })
    if (hideDuration) tabs.push({ id: 'duration', label: 'По длительности' })
    if (hideDemo)
      tabs.push({ id: 'demo', label: 'Демо-доступ', withoutOrder: true })
    return tabs
  }, [hidePopularity, hidePrice, hideDuration, hideDemo])

  return tabs
}

export default CatalogCoursesTab
